import {
  BILLING_FREQUENCY_OPTIONS,
  calculateLineItemTax,
} from "./billingPeriodUtils";
import type {
  InvoicePreviewLineItem,
  SaveBillingItemPayload,
  SaveBillingPayload,
} from "./types";

type DropdownOption = {
  value: string;
  label: string;
  subtitle?: string;
};

export function parseCcEmails(value: string): string[] {
  return value
    .split(",")
    .map(email => email.trim())
    .filter(Boolean);
}

function resolveCurrencyLabel(
  currencyValue: string,
  currencyOptions: DropdownOption[]
): string {
  const option = currencyOptions.find(item => item.value === currencyValue);
  if (!option) {
    return currencyValue;
  }

  return option.subtitle?.trim() ?? option.label.trim() ?? currencyValue;
}

function resolveBillingTypeLabel(
  billingTypeValue: string,
  billingTypeOptions: DropdownOption[]
): string {
  return (
    billingTypeOptions.find(option => option.value === billingTypeValue)
      ?.label ?? billingTypeValue
  );
}

function resolveFrequencyLabel(frequencyValue: string): string {
  return (
    BILLING_FREQUENCY_OPTIONS.find(option => option.value === frequencyValue)
      ?.label ?? frequencyValue
  );
}

function buildSaveBillingItem(
  item: InvoicePreviewLineItem,
  gstType: string | undefined,
  currencyOptions: DropdownOption[],
  billingTypeOptions: DropdownOption[]
): SaveBillingItemPayload {
  const tax = calculateLineItemTax(
    item.amount,
    item.gstApplicable,
    item.gstRate,
    gstType
  );

  return {
    amount: item.amount,
    currency: resolveCurrencyLabel(item.currency, currencyOptions),
    hsn_sac: item.hsnSac,
    qty: item.qty,
    cgst: item.gstApplicable && tax.isInstate ? tax.halfGstRate : 0,
    sgst: item.gstApplicable && tax.isInstate ? tax.halfGstRate : 0,
    igst: item.gstApplicable && !tax.isInstate ? tax.fullGstRate : 0,
    billing_type: resolveBillingTypeLabel(item.billingType, billingTypeOptions),
    frequency: resolveFrequencyLabel(item.frequency),
    binning_start_date: item.billingStartDate,
  };
}

export function buildSaveBillingPayload(input: {
  invoiceNumber: string;
  lineItems: InvoicePreviewLineItem[];
  billingTypeOptions: DropdownOption[];
  currencyOptions: DropdownOption[];
  clientGstType?: string;
  clientBillingEmail: string;
  ccEmails: string;
  accountantReviewEmail: string;
  reminderDays: string;
  gracePeriodDays: string;
  nextReminderDate: string;
  notes: string;
  projectId: string;
  clientName: string;
}): SaveBillingPayload {
  return {
    invoice_number: input.invoiceNumber.trim(),
    client_billing_email: input.clientBillingEmail.trim(),
    cc_emails: parseCcEmails(input.ccEmails),
    accountant_review_email: input.accountantReviewEmail.trim(),
    reminder: Number(input.reminderDays) || 0,
    grace_period: Number(input.gracePeriodDays) || 0,
    next_reminder_date: input.nextReminderDate.trim(),
    notes: input.notes.trim(),
    project: input.projectId.trim(),
    client_name: input.clientName.trim(),
    items: input.lineItems.map(item =>
      buildSaveBillingItem(
        item,
        input.clientGstType,
        input.currencyOptions,
        input.billingTypeOptions
      )
    ),
  };
}
